import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, ChevronUp, ChevronDown, Image as ImageIcon, Type } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { useInvitation } from "../context/InvitationContext";
import { Monogram } from "./Monogram";
import { THEME_PRESETS, BG_PRESETS, MUSIC_PRESETS, FONT_DISPLAY_OPTIONS, FONT_NAMES_OPTIONS } from "../lib/presets";

const genId = () => `${Date.now()}${Math.random().toString(36).slice(2, 6)}`;

const Field = ({ label, path, data, updateField, area }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-[11px] tracking-wide text-neutral-500">{label}</Label>
    {area ? (
      <Textarea dir="auto" data-testid={`editor-${path}`} value={data[path] || ""} rows={2} onChange={(e) => updateField(path, e.target.value)} className="text-sm" />
    ) : (
      <Input dir="auto" data-testid={`editor-${path}`} value={data[path] || ""} onChange={(e) => updateField(path, e.target.value)} className="text-sm" />
    )}
  </div>
);

const POSITIONS = [
  { value: "envelope", label: "On the tap-to-open (envelope) page" },
  { value: "afterIntro", label: "After Bismillah screen" },
  { value: "top", label: "After greeting" },
  { value: "afterCouple", label: "After couple names" },
  { value: "afterEvent", label: "After event details" },
  { value: "afterCountdown", label: "Right after the countdown" },
  { value: "afterContacts", label: "After contacts" },
  { value: "beforeRsvp", label: "Before RSVP" },
  { value: "bottom", label: "At the very bottom" },
];

const SIZE_GROUPS = [
  { key: "monogram", label: "Envelope monogram" },
  { key: "arabic", label: "Arabic verses" },
  { key: "names", label: "Couple names" },
  { key: "lastNames", label: "Last names" },
  { key: "connector", label: "Connector word" },
  { key: "parents", label: "Parents lines" },
  { key: "headings", label: "Headings & dates" },
  { key: "venue", label: "Venue name" },
  { key: "countdown", label: "Countdown numbers" },
  { key: "labels", label: "Small labels" },
  { key: "body", label: "Body text" },
];

export const EditorPanel = ({ open, onOpenChange }) => {
  const { data, setData, updateField, addItem, removeItem, updateItem, moveItem, save, reset, listBackups, restoreBackup, saving, API, eventKey } = useInvitation();
  const [backups, setBackups] = useState(null);
  const [restoring, setRestoring] = useState(false);
  if (!data) return null;
  const c = data.colors;

  const exportContent = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invitation-${eventKey || "main"}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Content exported. Now Import this file on your live site.");
  };

  const importContent = (file) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const parsed = JSON.parse(reader.result);
        if (!parsed || typeof parsed !== "object" || !parsed.groomName) {
          toast.error("That doesn't look like an invitation file.");
          return;
        }
        delete parsed.id;
        setData(parsed);
        const ok = await save(parsed);
        toast[ok ? "success" : "error"](ok ? "Imported & published!" : "Import failed to save.");
      } catch (e) {
        toast.error("Could not read that file.");
      }
    };
    reader.readAsText(file);
  };

  const openBackups = async () => {
    if (backups !== null) return setBackups(null);
    try {
      const list = await listBackups();
      setBackups(list);
    } catch (e) {
      toast.error("Couldn't load backups");
    }
  };

  const doRestore = async (id) => {
    setRestoring(true);
    try {
      await restoreBackup(id);
      toast.success("Previous version restored");
      setBackups(null);
      onOpenChange(false);
    } catch (e) {
      toast.error("Restore failed");
    } finally {
      setRestoring(false);
    }
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const handleSave = async () => {
    const ok = await save();
    toast[ok ? "success" : "error"](ok ? "Invitation saved!" : "Save failed");
    if (ok) onOpenChange(false);
  };

  const uploadTo = async (file, onUrl, maxMb) => {
    if (file.size > maxMb * 1024 * 1024) return toast.error(`File too large (max ${maxMb}MB)`);
    const fd = new FormData();
    fd.append("file", file);
    const tId = toast.loading("Uploading…");
    try {
      const res = await axios.post(`${API}/upload`, fd, { withCredentials: true });
      onUrl(res.data.url);
      toast.success("Uploaded", { id: tId });
    } catch (e) {
      toast.error("Upload failed", { id: tId });
    }
  };
  const uploadImg = (field, file) => uploadTo(file, (url) => updateField(field, url), 10);
  const uploadBlockImg = (id, file) => uploadTo(file, (url) => updateItem("customBlocks", id, "imageUrl", url), 10);
  const uploadAudio = (file) => uploadTo(file, (url) => updateField("musicUrl", url), 15);
  const uploadVideo = (file) => uploadTo(file, (url) => updateField("introVideo", url), 40);

  const addBlock = (type) =>
    addItem("customBlocks", {
      id: genId(),
      type,
      heading: type === "image" ? "" : "New heading",
      body: type === "image" ? "" : "Your text here",
      imageUrl: "",
      arabic: false,
      align: "center",
      position: "beforeRsvp",
    });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-0" data-testid="editor-panel">
        <SheetHeader className="px-5 pt-5 pb-3 sticky top-0 bg-white/90 backdrop-blur-xl z-10 border-b">
          <SheetTitle className="font-display text-2xl tracking-tight" style={{ color: c.primary }}>Customize Invitation</SheetTitle>
          <SheetDescription className="text-xs text-neutral-500">Edit everything, add text &amp; image blocks, contacts, theme, backgrounds and music. Arabic typing supported.</SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="names" className="px-5 py-4">
          <TabsList className="grid grid-cols-4 h-auto w-full mb-4 gap-1">
            <TabsTrigger value="names" className="text-xs" data-testid="tab-names">Names</TabsTrigger>
            <TabsTrigger value="event" className="text-xs" data-testid="tab-event">Event</TabsTrigger>
            <TabsTrigger value="blocks" className="text-xs" data-testid="tab-blocks">Photos</TabsTrigger>
            <TabsTrigger value="contact" className="text-xs" data-testid="tab-contact">Contact</TabsTrigger>
            <TabsTrigger value="extras" className="text-xs" data-testid="tab-extras">Extras</TabsTrigger>
            <TabsTrigger value="theme" className="text-xs" data-testid="tab-theme">Theme</TabsTrigger>
            <TabsTrigger value="sizes" className="text-xs" data-testid="tab-sizes">Sizes</TabsTrigger>
            <TabsTrigger value="media" className="text-xs" data-testid="tab-media">Media</TabsTrigger>
          </TabsList>

          {/* EXTRAS */}
          <TabsContent value="extras" className="flex flex-col gap-5">
            <div className="flex flex-col gap-2 rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">Du'a / Blessing (after countdown)</Label>
                <button data-testid="toggle-dua" onClick={() => updateField("duaEnabled", !data.duaEnabled)} className="text-[11px] px-2 py-1 rounded-md border" style={{ background: data.duaEnabled ? c.primary : "transparent", color: data.duaEnabled ? "#fff" : c.text }}>{data.duaEnabled ? "On" : "Off"}</button>
              </div>
              <Field label="Intro line" path="duaIntro" data={data} updateField={updateField} area />
              <Field label="Du'a (arabic)" path="duaArabic" data={data} updateField={updateField} area />
              <Field label="Ameen line (arabic)" path="duaAmeen" data={data} updateField={updateField} />
              <Field label="Translation" path="duaTranslation" data={data} updateField={updateField} area />
            </div>

            <div className="flex flex-col gap-2 rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">Camera notice (before RSVP)</Label>
                <button data-testid="toggle-camera" onClick={() => updateField("cameraEnabled", !data.cameraEnabled)} className="text-[11px] px-2 py-1 rounded-md border" style={{ background: data.cameraEnabled ? c.primary : "transparent", color: data.cameraEnabled ? "#fff" : c.text }}>{data.cameraEnabled ? "On" : "Off"}</button>
              </div>
              <Field label="Notice text" path="cameraText" data={data} updateField={updateField} area />
            </div>

            <div className="flex flex-col gap-2 rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">Compliments (after RSVP)</Label>
                <button data-testid="toggle-compliments" onClick={() => updateField("complimentsEnabled", !data.complimentsEnabled)} className="text-[11px] px-2 py-1 rounded-md border" style={{ background: data.complimentsEnabled ? c.primary : "transparent", color: data.complimentsEnabled ? "#fff" : c.text }}>{data.complimentsEnabled ? "On" : "Off"}</button>
              </div>
              <Field label="Title" path="complimentsTitle" data={data} updateField={updateField} />
              {(data.complimentsNames || []).map((n, idx) => (
                <div key={n.id} className="flex gap-2 items-center" data-testid={`compliment-${idx}`}>
                  <Input dir="auto" value={n.name || ""} placeholder="Family name" onChange={(e) => updateItem("complimentsNames", n.id, "name", e.target.value)} className="text-sm" />
                  <button data-testid={`remove-compliment-${idx}`} onClick={() => removeItem("complimentsNames", n.id)} className="p-1 text-red-500"><Trash2 size={15} /></button>
                </div>
              ))}
              <Button size="sm" variant="outline" data-testid="add-compliment" onClick={() => addItem("complimentsNames", { id: genId(), name: "" })}><Plus size={14} className="mr-1" /> Add name</Button>
            </div>

            <div className="flex flex-col gap-2 rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">Map / address (after RSVP)</Label>
                <button data-testid="toggle-map" onClick={() => updateField("mapEnabled", !data.mapEnabled)} className="text-[11px] px-2 py-1 rounded-md border" style={{ background: data.mapEnabled ? c.primary : "transparent", color: data.mapEnabled ? "#fff" : c.text }}>{data.mapEnabled ? "On" : "Off"}</button>
              </div>
              <Field label="Location / address to search on Google Maps" path="mapQuery" data={data} updateField={updateField} area />
            </div>

            <div className="flex flex-col gap-2 rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">Gift note (highlighted, at the end)</Label>
                <button data-testid="toggle-gift" onClick={() => updateField("giftEnabled", !data.giftEnabled)} className="text-[11px] px-2 py-1 rounded-md border" style={{ background: data.giftEnabled ? c.primary : "transparent", color: data.giftEnabled ? "#fff" : c.text }}>{data.giftEnabled ? "On" : "Off"}</button>
              </div>
              <Field label="Title" path="giftTitle" data={data} updateField={updateField} />
              <Field label="Gift message" path="giftText" data={data} updateField={updateField} area />
            </div>
          </TabsContent>

          {/* SIZES */}
          <TabsContent value="sizes" className="flex flex-col gap-4">
            <p className="text-xs text-neutral-500">Fine-tune the size and thickness of each piece of text. 100% is the default.</p>
            {SIZE_GROUPS.map((g) => {
              const val = (data.sizes && data.sizes[g.key]) || 1;
              const wVal = (data.weights && data.weights[g.key]) || 400;
              return (
                <div key={g.key} className="flex flex-col gap-1.5 border-b border-neutral-100 pb-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-neutral-600">{g.label}</span>
                    <span className="text-xs text-neutral-500">{Math.round(val * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0.6"
                    max="1.8"
                    step="0.05"
                    data-testid={`size-${g.key}`}
                    value={val}
                    onChange={(e) => updateField(`sizes.${g.key}`, parseFloat(e.target.value))}
                    className="w-full"
                    style={{ accentColor: c.primary }}
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-neutral-400 w-12">Weight</span>
                    <select
                      data-testid={`weight-${g.key}`}
                      value={wVal}
                      onChange={(e) => updateField(`weights.${g.key}`, parseInt(e.target.value, 10))}
                      className="flex-1 text-xs border rounded-md px-2 py-1.5 bg-white"
                      style={{ fontWeight: wVal }}
                    >
                      <option value={300}>Thin / Light</option>
                      <option value={400}>Regular</option>
                      <option value={500}>Medium</option>
                      <option value={600}>Semibold</option>
                      <option value={700}>Bold</option>
                    </select>
                  </div>
                </div>
              );
            })}
          </TabsContent>

          {/* NAMES */}
          <TabsContent value="names" className="flex flex-col gap-3">
            <Field label="Envelope monogram (e.g. M&M)" path="monogram" data={data} updateField={updateField} />
            <div className="flex flex-col gap-1.5">
              <Label className="text-[11px] tracking-wide text-neutral-500">Monogram font</Label>
              <select
                data-testid="font-monogram-select-names"
                value={data.fontMonogram || "Cormorant Garamond"}
                onChange={(e) => updateField("fontMonogram", e.target.value)}
                className="text-lg border rounded-md px-2 py-2 bg-white"
                style={{ fontFamily: `'${data.fontMonogram}'` }}
              >
                {FONT_NAMES_OPTIONS.map((f) => (
                  <option key={f} value={f} style={{ fontFamily: `'${f}'` }}>{f}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-[11px] tracking-wide text-neutral-500">Logo / crest image (shows instead of the "{data.monogram || "MM"}" text)</Label>
              {data.logoImage && <img src={data.logoImage} alt="logo" className="h-20 w-20 object-contain self-start" />}
              <Input dir="auto" data-testid="editor-logoImage" value={data.logoImage || ""} onChange={(e) => updateField("logoImage", e.target.value)} className="text-xs" placeholder="Image URL (leave blank to use text monogram)" />
              <div className="flex gap-3">
                <label className="text-xs underline cursor-pointer" style={{ color: c.secondary }}>Upload image<input type="file" accept="image/*" className="hidden" data-testid="upload-logo" onChange={(e) => e.target.files[0] && uploadImg("logoImage", e.target.files[0])} /></label>
                {data.logoImage && <button className="text-xs underline" style={{ color: c.secondary }} onClick={() => updateField("logoImage", "")}>Remove (use text)</button>}
              </div>
            </div>
            <Field label="Tap to open text" path="tapToOpenText" data={data} updateField={updateField} />
            <div className="h-px bg-neutral-200 my-1" />
            <Field label="Bismillah subtitle" path="bismillahSubtitle" data={data} updateField={updateField} area />
            <Field label="Arabic greeting" path="greetingArabic" data={data} updateField={updateField} area />
            <Field label="Greeting subtitle" path="greetingSubtitle" data={data} updateField={updateField} />
            <Field label="Blessing line (english)" path="blessingLine" data={data} updateField={updateField} />
            <Field label="Blessing (arabic)" path="blessingArabic" data={data} updateField={updateField} />
            <Field label="Host / intro text" path="hostText" data={data} updateField={updateField} area />
            <div className="h-px bg-neutral-200 my-1" />
            <Field label="Groom first name" path="groomName" data={data} updateField={updateField} />
            <Field label="Groom last name" path="groomLastName" data={data} updateField={updateField} />
            <Field label="Groom parents" path="groomParents" data={data} updateField={updateField} />
            <div className="h-px bg-neutral-200 my-1" />
            <Field label="Bride first name" path="brideName" data={data} updateField={updateField} />
            <Field label="Bride last name" path="brideLastName" data={data} updateField={updateField} />
            <Field label="Bride parents" path="brideParents" data={data} updateField={updateField} />
            <Field label="Connector between names" path="coupleConnector" data={data} updateField={updateField} />
            <div className="h-px bg-neutral-200 my-1" />
            <Field label="Footer (arabic)" path="footerArabic" data={data} updateField={updateField} />
            <Field label="Footer name (after جزاك الله خير)" path="footerName" data={data} updateField={updateField} />
          </TabsContent>

          {/* EVENT */}
          <TabsContent value="event" className="flex flex-col gap-3">
            <Field label="Event name (heading)" path="eventName" data={data} updateField={updateField} />
            <Field label="Text under the day" path="eventSubtitle" data={data} updateField={updateField} area />
            <Field label="Date (Gregorian)" path="dateGregorian" data={data} updateField={updateField} />
            <Field label="Date (Hijri)" path="dateHijri" data={data} updateField={updateField} />
            <Field label="Day" path="dayName" data={data} updateField={updateField} />
            <Field label="Time" path="timeText" data={data} updateField={updateField} />
            <Field label="Venue name" path="venueName" data={data} updateField={updateField} />
            <Field label="Venue address" path="venueAddress" data={data} updateField={updateField} area />
            <div className="flex flex-col gap-1.5">
              <Label className="text-[11px] tracking-wide text-neutral-500">Venue font</Label>
              <select
                data-testid="font-venue-select"
                value={data.fontVenue || "Cormorant Garamond"}
                onChange={(e) => updateField("fontVenue", e.target.value)}
                className="text-base border rounded-md px-2 py-2 bg-white"
                style={{ fontFamily: `'${data.fontVenue}'` }}
              >
                {[...FONT_DISPLAY_OPTIONS, ...FONT_NAMES_OPTIONS.filter((f) => !FONT_DISPLAY_OPTIONS.includes(f))].map((f) => (
                  <option key={f} value={f} style={{ fontFamily: `'${f}'` }}>{f}</option>
                ))}
              </select>
              <p className="text-xl" style={{ fontFamily: `'${data.fontVenue}'`, color: c.primary }}>{data.venueName}</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-[11px] tracking-wide text-neutral-500">Countdown target</Label>
              <Input type="datetime-local" data-testid="editor-eventDateTime" value={(data.eventDateTime || "").slice(0, 16)} onChange={(e) => updateField("eventDateTime", e.target.value)} className="text-sm" />
            </div>
            <Field label="Countdown title" path="countdownTitle" data={data} updateField={updateField} />
            <div className="h-px bg-neutral-200 my-1" />
            <Field label="RSVP title" path="rsvpTitle" data={data} updateField={updateField} />
            <Field label="RSVP subtitle" path="rsvpSubtitle" data={data} updateField={updateField} />
            <Field label="RSVP 'attending' label" path="rsvpAttendLabel" data={data} updateField={updateField} />
            <Field label="RSVP 'not attending' label" path="rsvpDeclineLabel" data={data} updateField={updateField} />
          </TabsContent>

          {/* BLOCKS */}
          <TabsContent value="blocks" className="flex flex-col gap-3">
            <p className="text-xs text-neutral-500">Add photos or text and place them anywhere in the invitation.</p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1" data-testid="add-image-block" onClick={() => addBlock("image")}><ImageIcon size={14} className="mr-1" /> Add Photo</Button>
              <Button size="sm" variant="outline" className="flex-1" data-testid="add-text-block" onClick={() => addBlock("text")}><Type size={14} className="mr-1" /> Add Text</Button>
            </div>
            {(data.customBlocks || []).map((b, idx) => (
              <div key={b.id} className="rounded-lg border p-3 flex flex-col gap-2" data-testid={`block-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-wide text-neutral-500 flex items-center gap-1">
                    {b.type === "image" ? <ImageIcon size={12} /> : <Type size={12} />} {b.type}
                  </span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveItem("customBlocks", b.id, -1)} className="p-1 text-neutral-500 hover:text-black"><ChevronUp size={15} /></button>
                    <button onClick={() => moveItem("customBlocks", b.id, 1)} className="p-1 text-neutral-500 hover:text-black"><ChevronDown size={15} /></button>
                    <button data-testid={`remove-block-${idx}`} onClick={() => removeItem("customBlocks", b.id)} className="p-1 text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                  </div>
                </div>
                <select value={b.position || "beforeRsvp"} onChange={(e) => updateItem("customBlocks", b.id, "position", e.target.value)} className="text-xs border rounded-md px-2 py-1.5 bg-white">
                  {POSITIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-neutral-400">Fine move</span>
                  <button data-testid={`nudge-up-${idx}`} onClick={() => updateItem("customBlocks", b.id, "offsetY", (b.offsetY || 0) - 24)} className="px-2 py-1 rounded-md border hover:bg-neutral-50">↑ Up</button>
                  <button data-testid={`nudge-down-${idx}`} onClick={() => updateItem("customBlocks", b.id, "offsetY", (b.offsetY || 0) + 24)} className="px-2 py-1 rounded-md border hover:bg-neutral-50">↓ Down</button>
                  <span className="text-neutral-400">{b.offsetY || 0}px</span>
                  {(b.offsetY || 0) !== 0 && <button onClick={() => updateItem("customBlocks", b.id, "offsetY", 0)} className="underline text-neutral-500">reset</button>}
                </div>
                {b.type === "image" ? (
                  <>
                    {b.imageUrl && <img src={b.imageUrl} alt="preview" className="w-full h-24 object-cover rounded-md" />}
                    <Input dir="auto" value={b.imageUrl || ""} placeholder="Image URL" onChange={(e) => updateItem("customBlocks", b.id, "imageUrl", e.target.value)} className="text-xs" />
                    <label className="text-xs underline cursor-pointer" style={{ color: c.secondary }}>
                      Upload image
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadBlockImg(b.id, e.target.files[0])} />
                    </label>
                    <Input dir="auto" value={b.heading || ""} placeholder="Caption (optional)" onChange={(e) => updateItem("customBlocks", b.id, "heading", e.target.value)} className="text-xs" />
                  </>
                ) : (
                  <>
                    <Input dir="auto" value={b.heading || ""} placeholder="Heading" onChange={(e) => updateItem("customBlocks", b.id, "heading", e.target.value)} className="text-sm" />
                    <Textarea dir="auto" value={b.body || ""} rows={2} placeholder="Text" onChange={(e) => updateItem("customBlocks", b.id, "body", e.target.value)} className="text-sm" />
                    <div className="flex gap-2">
                      <button onClick={() => updateItem("customBlocks", b.id, "arabic", !b.arabic)} className="text-xs px-2 py-1 rounded-md border" style={{ background: b.arabic ? c.primary : "transparent", color: b.arabic ? "#fff" : c.text }}>Arabic font</button>
                      <button onClick={() => updateItem("customBlocks", b.id, "align", b.align === "left" ? "center" : "left")} className="text-xs px-2 py-1 rounded-md border">Align: {b.align || "center"}</button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </TabsContent>

          {/* CONTACT */}
          <TabsContent value="contact" className="flex flex-col gap-3">
            <Field label="Contacts title" path="contactsTitle" data={data} updateField={updateField} />
            {(data.contacts || []).map((ct, idx) => (
              <div key={ct.id} className="rounded-lg border p-3 flex flex-col gap-2" data-testid={`contact-${idx}`}>
                <div className="flex justify-end">
                  <button data-testid={`remove-contact-${idx}`} onClick={() => removeItem("contacts", ct.id)} className="p-1 text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                </div>
                <Input dir="auto" value={ct.name || ""} placeholder="Name" onChange={(e) => updateItem("contacts", ct.id, "name", e.target.value)} className="text-sm" />
                <Input dir="auto" value={ct.phone || ""} placeholder="Phone (+91 ...)" onChange={(e) => updateItem("contacts", ct.id, "phone", e.target.value)} className="text-sm" />
              </div>
            ))}
            <Button size="sm" variant="outline" data-testid="add-contact" onClick={() => addItem("contacts", { id: genId(), name: "", phone: "" })}><Plus size={14} className="mr-1" /> Add contact</Button>
          </TabsContent>

          {/* THEME */}
          <TabsContent value="theme" className="flex flex-col gap-4">
            <Label className="text-[11px] tracking-wide text-neutral-500">Display size</Label>
            <div className="flex rounded-lg overflow-hidden border" data-testid="display-mode-toggle">
              {[
                { label: "Portrait phone", val: true },
                { label: "Full width", val: false },
              ].map((o) => (
                <button
                  key={o.label}
                  data-testid={`display-mode-${o.val}`}
                  onClick={() => updateField("phoneMode", o.val)}
                  className="flex-1 text-xs py-2 font-body"
                  style={{ background: !!data.phoneMode === o.val ? c.primary : "transparent", color: !!data.phoneMode === o.val ? "#fff" : c.text }}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-[11px] tracking-wide text-neutral-500">Auto-play (glide through by itself)</Label>
              <button data-testid="toggle-autoscroll" onClick={() => updateField("autoScroll", !data.autoScroll)} className="text-[11px] px-2 py-1 rounded-md border" style={{ background: data.autoScroll ? c.primary : "transparent", color: data.autoScroll ? "#fff" : c.text }}>{data.autoScroll ? "On" : "Off"}</button>
            </div>
            {data.autoScroll && (
              <div className="flex rounded-lg overflow-hidden border" data-testid="autoscroll-speed-toggle">
                {[["slow", "Slow"], ["medium", "Medium"], ["fast", "Fast"]].map(([val, label]) => (
                  <button key={val} data-testid={`autoscroll-speed-${val}`} onClick={() => updateField("autoScrollSpeed", val)} className="flex-1 text-xs py-1.5 font-body" style={{ background: (data.autoScrollSpeed || "medium") === val ? c.primary : "transparent", color: (data.autoScrollSpeed || "medium") === val ? "#fff" : c.text }}>{label}</button>
                ))}
              </div>
            )}
            <div className="h-px bg-neutral-200" />
            <Label className="text-[11px] tracking-wide text-neutral-500">Fonts</Label>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs text-neutral-600">Headings &amp; dates</span>
              <select
                data-testid="font-display-select"
                value={data.fontDisplay || "Cormorant Garamond"}
                onChange={(e) => updateField("fontDisplay", e.target.value)}
                className="text-sm border rounded-md px-2 py-2 bg-white"
                style={{ fontFamily: `'${data.fontDisplay}'` }}
              >
                {FONT_DISPLAY_OPTIONS.map((f) => (
                  <option key={f} value={f} style={{ fontFamily: `'${f}'` }}>{f}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs text-neutral-600">Couple names &amp; monogram</span>
              <select
                data-testid="font-names-select"
                value={data.fontNames || "Cormorant Garamond"}
                onChange={(e) => updateField("fontNames", e.target.value)}
                className="text-lg border rounded-md px-2 py-2 bg-white"
                style={{ fontFamily: `'${data.fontNames}'` }}
              >
                {FONT_NAMES_OPTIONS.map((f) => (
                  <option key={f} value={f} style={{ fontFamily: `'${f}'` }}>{f}</option>
                ))}
              </select>
              <p className="text-2xl mt-1" style={{ fontFamily: `'${data.fontNames}'`, color: c.primary }}>
                {data.groomName} &amp; {data.brideName}
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs text-neutral-600">Monogram (envelope "MM")</span>
              <select
                data-testid="font-monogram-select"
                value={data.fontMonogram || "Cormorant Garamond"}
                onChange={(e) => updateField("fontMonogram", e.target.value)}
                className="text-lg border rounded-md px-2 py-2 bg-white"
                style={{ fontFamily: `'${data.fontMonogram}'` }}
              >
                {FONT_NAMES_OPTIONS.map((f) => (
                  <option key={f} value={f} style={{ fontFamily: `'${f}'` }}>{f}</option>
                ))}
              </select>
              <p className="text-3xl mt-1 italic" style={{ fontFamily: `'${data.fontMonogram}'`, color: c.accent }}>
                {data.monogram || "MM"}
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs text-neutral-600">Monogram style</span>
              <div className="grid grid-cols-2 gap-2" data-testid="monogram-style-grid">
                {[["plain", "Plain"], ["interlocked", "Interlocked"], ["ampersand", "Ampersand"], ["crest", "Crest"]].map(([val, label]) => (
                  <button
                    key={val}
                    data-testid={`monogram-style-${val}`}
                    onClick={() => updateField("monogramStyle", val)}
                    className="flex flex-col items-center justify-center gap-1 rounded-lg border h-20 hover:shadow-sm transition-shadow"
                    style={{ borderColor: (data.monogramStyle || "plain") === val ? c.secondary : "#e5e5e5", borderWidth: (data.monogramStyle || "plain") === val ? 2 : 1 }}
                  >
                    <Monogram data={{ ...data, monogramStyle: val }} size={22} />
                    <span className="text-[10px] text-neutral-500">{label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-neutral-600">Overall text size</span>
                <span className="text-xs text-neutral-500">{Math.round((data.fontScale || 1) * 100)}%</span>
              </div>
              <input
                type="range"
                min="0.8"
                max="1.4"
                step="0.05"
                data-testid="font-scale-slider"
                value={data.fontScale || 1}
                onChange={(e) => updateField("fontScale", parseFloat(e.target.value))}
                className="w-full accent-current"
                style={{ color: c.primary }}
              />
            </div>
            <div className="h-px bg-neutral-200" />
            <Label className="text-[11px] tracking-wide text-neutral-500">Theme presets</Label>
            <div className="grid grid-cols-2 gap-2">
              {THEME_PRESETS.map((p) => (
                <button key={p.name} data-testid={`theme-preset-${p.name}`} onClick={() => updateField("colors", p.colors)} className="flex items-center gap-2 rounded-lg border p-2 hover:shadow-sm transition-shadow">
                  <span className="flex -space-x-1">
                    {[p.colors.primary, p.colors.secondary, p.colors.accent].map((col, i) => (<span key={i} className="w-4 h-4 rounded-full border border-white" style={{ background: col }} />))}
                  </span>
                  <span className="text-[11px] text-left leading-tight">{p.name}</span>
                </button>
              ))}
            </div>
            <div className="h-px bg-neutral-200" />
            <Label className="text-[11px] tracking-wide text-neutral-500">Custom colors</Label>
            {[["Primary", "primary"], ["Secondary / accent text", "secondary"], ["Gold accent", "accent"], ["Main text", "text"], ["Background", "bg"]].map(([label, key]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-xs text-neutral-600">{label}</span>
                <input type="color" data-testid={`color-${key}`} value={c[key]} onChange={(e) => updateField(`colors.${key}`, e.target.value)} className="w-10 h-8 rounded cursor-pointer border" />
              </div>
            ))}
          </TabsContent>

          {/* MEDIA */}
          <TabsContent value="media" className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label className="text-[11px] tracking-wide text-neutral-500">Intro background (Bismillah screen)</Label>
              <div className="grid grid-cols-3 gap-2">
                {BG_PRESETS.map((u) => (
                  <button key={u} data-testid="bg-intro-preset" onClick={() => updateField("bgImageIntro", u)} className="h-14 rounded-md bg-cover bg-center border-2" style={{ backgroundImage: `url(${u})`, borderColor: data.bgImageIntro === u ? c.secondary : "transparent" }} />
                ))}
              </div>
              <Input dir="auto" data-testid="editor-bgImageIntro" value={data.bgImageIntro} onChange={(e) => updateField("bgImageIntro", e.target.value)} className="text-xs" placeholder="Image URL" />
              <label className="text-xs underline cursor-pointer" style={{ color: c.secondary }}>Upload image<input type="file" accept="image/*" className="hidden" data-testid="upload-bg-intro" onChange={(e) => e.target.files[0] && uploadImg("bgImageIntro", e.target.files[0])} /></label>
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-[11px] tracking-wide text-neutral-500">Envelope / first page background (optional)</Label>
              <div className="grid grid-cols-3 gap-2">
                {BG_PRESETS.map((u) => (
                  <button key={u} data-testid="bg-envelope-preset" onClick={() => updateField("bgImageEnvelope", u)} className="h-14 rounded-md bg-cover bg-center border-2" style={{ backgroundImage: `url(${u})`, borderColor: data.bgImageEnvelope === u ? c.secondary : "transparent" }} />
                ))}
              </div>
              <Input dir="auto" data-testid="editor-bgImageEnvelope" value={data.bgImageEnvelope} onChange={(e) => updateField("bgImageEnvelope", e.target.value)} className="text-xs" placeholder="Image URL (leave blank for plain)" />
              <div className="flex gap-3">
                <label className="text-xs underline cursor-pointer" style={{ color: c.secondary }}>Upload image<input type="file" accept="image/*" className="hidden" data-testid="upload-bg-envelope" onChange={(e) => e.target.files[0] && uploadImg("bgImageEnvelope", e.target.files[0])} /></label>
                {data.bgImageEnvelope && <button className="text-xs underline" style={{ color: c.secondary }} onClick={() => updateField("bgImageEnvelope", "")}>Remove</button>}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label className="text-[11px] tracking-wide text-neutral-500">Overall invitation background (optional)</Label>
              <div className="grid grid-cols-3 gap-2">
                {BG_PRESETS.map((u) => (
                  <button key={u} data-testid="bg-page-preset" onClick={() => updateField("bgImagePage", u)} className="h-14 rounded-md bg-cover bg-center border-2" style={{ backgroundImage: `url(${u})`, borderColor: data.bgImagePage === u ? c.secondary : "transparent" }} />
                ))}
              </div>
              <Input dir="auto" data-testid="editor-bgImagePage" value={data.bgImagePage} onChange={(e) => updateField("bgImagePage", e.target.value)} className="text-xs" placeholder="Image URL (leave blank for plain)" />
              <div className="flex gap-3">
                <label className="text-xs underline cursor-pointer" style={{ color: c.secondary }}>Upload image<input type="file" accept="image/*" className="hidden" data-testid="upload-bg-page" onChange={(e) => e.target.files[0] && uploadImg("bgImagePage", e.target.files[0])} /></label>
                {data.bgImagePage && <button className="text-xs underline" style={{ color: c.secondary }} onClick={() => updateField("bgImagePage", "")}>Remove</button>}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-[11px] tracking-wide text-neutral-500">Couple page decoration</Label>
              <div className="flex rounded-md overflow-hidden border" style={{ borderColor: `${c.primary}44` }}>
                {[["sides", "Side flowers"], ["corners", "Corner frames"], ["none", "None"]].map(([val, label]) => (
                  <button key={val} data-testid={`couple-decor-${val}`} onClick={() => updateField("coupleDecor", val)} className="flex-1 text-xs py-1.5 font-body" style={{ background: (data.coupleDecor || "sides") === val ? c.primary : "transparent", color: (data.coupleDecor || "sides") === val ? "#fff" : c.text }}>{label}</button>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-[11px] tracking-wide text-neutral-500">Floral accent (couple section)</Label>
              <Input dir="auto" data-testid="editor-floralAccent" value={data.floralAccent} onChange={(e) => updateField("floralAccent", e.target.value)} className="text-xs" placeholder="Image URL" />
              <label className="text-xs underline cursor-pointer" style={{ color: c.secondary }}>Upload image<input type="file" accept="image/*" className="hidden" data-testid="upload-floral" onChange={(e) => e.target.files[0] && uploadImg("floralAccent", e.target.files[0])} /></label>
            </div>
            <div className="flex flex-col gap-2 rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <Label className="text-[11px] tracking-wide text-neutral-500">Couple names background</Label>
                <button data-testid="toggle-cherry" onClick={() => updateField("cherryBlossom", !data.cherryBlossom)} className="text-[11px] px-2 py-1 rounded-md border" style={{ background: data.cherryBlossom ? c.primary : "transparent", color: data.cherryBlossom ? "#fff" : c.text }}>Effect {data.cherryBlossom ? "On" : "Off"}</button>
              </div>
              {data.cherryBlossom && (
                <>
                  <div className="flex rounded-lg overflow-hidden border" data-testid="overlay-type-toggle">
                    {[["petals", "Petals"], ["sparkles", "Sparkles"], ["hearts", "Hearts"]].map(([val, label]) => (
                      <button key={val} data-testid={`overlay-type-${val}`} onClick={() => updateField("overlayType", val)} className="flex-1 text-xs py-1.5 font-body" style={{ background: (data.overlayType || "petals") === val ? c.primary : "transparent", color: (data.overlayType || "petals") === val ? "#fff" : c.text }}>{label}</button>
                    ))}
                  </div>
                  <div className="flex rounded-lg overflow-hidden border" data-testid="petal-density-toggle">
                    {[["light", "Light"], ["medium", "Medium"], ["heavy", "Heavy"]].map(([val, label]) => (
                      <button key={val} data-testid={`petal-density-${val}`} onClick={() => updateField("petalDensity", val)} className="flex-1 text-xs py-1.5 font-body" style={{ background: (data.petalDensity || "medium") === val ? c.primary : "transparent", color: (data.petalDensity || "medium") === val ? "#fff" : c.text }}>{label}</button>
                    ))}
                  </div>
                </>
              )}
              <div className="grid grid-cols-3 gap-2">
                {BG_PRESETS.map((u) => (
                  <button key={u} data-testid="bg-couple-preset" onClick={() => updateField("bgImageCouple", u)} className="h-14 rounded-md bg-cover bg-center border-2" style={{ backgroundImage: `url(${u})`, borderColor: data.bgImageCouple === u ? c.secondary : "transparent" }} />
                ))}
              </div>
              <Input dir="auto" data-testid="editor-bgImageCouple" value={data.bgImageCouple || ""} onChange={(e) => updateField("bgImageCouple", e.target.value)} className="text-xs" placeholder="Image URL (leave blank for plain)" />
              <div className="flex gap-3">
                <label className="text-xs underline cursor-pointer" style={{ color: c.secondary }}>Upload image<input type="file" accept="image/*" className="hidden" data-testid="upload-bg-couple" onChange={(e) => e.target.files[0] && uploadImg("bgImageCouple", e.target.files[0])} /></label>
                {data.bgImageCouple && <button className="text-xs underline" style={{ color: c.secondary }} onClick={() => updateField("bgImageCouple", "")}>Remove</button>}
              </div>
            </div>
            <div className="h-px bg-neutral-200" />
            <div className="flex flex-col gap-3 rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">Corner florals on slides</Label>
                <button data-testid="toggle-corners" onClick={() => updateField("cornerFrames", !data.cornerFrames)} className="text-[11px] px-2 py-1 rounded-md border" style={{ background: data.cornerFrames ? c.primary : "transparent", color: data.cornerFrames ? "#fff" : c.text }}>{data.cornerFrames ? "On" : "Off"}</button>
              </div>
              {data.cornerFrames && (
                <div className="flex flex-col gap-3 rounded-md bg-neutral-50 p-2.5">
                  <div className="flex flex-col gap-1">
                    <div className="flex justify-between"><Label className="text-[11px] text-neutral-500">Flower size</Label><span className="text-[11px] text-neutral-400">{data.cornerSize ?? 30}%</span></div>
                    <input type="range" min="12" max="55" step="1" data-testid="corner-size" value={data.cornerSize ?? 30} onChange={(e) => updateField("cornerSize", parseInt(e.target.value, 10))} style={{ accentColor: c.primary }} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex justify-between"><Label className="text-[11px] text-neutral-500">Position (into corner ↔ over text)</Label><span className="text-[11px] text-neutral-400">{data.cornerInset ?? -18}px</span></div>
                    <input type="range" min="-60" max="20" step="2" data-testid="corner-inset" value={data.cornerInset ?? -18} onChange={(e) => updateField("cornerInset", parseInt(e.target.value, 10))} style={{ accentColor: c.primary }} />
                    <span className="text-[10px] text-neutral-400">Lower (left) tucks flowers into the corners & off the text. Higher moves them inward.</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex justify-between"><Label className="text-[11px] text-neutral-500">Opacity</Label><span className="text-[11px] text-neutral-400">{data.cornerOpacity ?? 90}%</span></div>
                    <input type="range" min="20" max="100" step="5" data-testid="corner-opacity" value={data.cornerOpacity ?? 90} onChange={(e) => updateField("cornerOpacity", parseInt(e.target.value, 10))} style={{ accentColor: c.primary }} />
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-1">
                <div className="flex justify-between"><Label className="text-[11px] text-neutral-500">Background lighten</Label><span className="text-[11px] text-neutral-400">{data.bgLighten ?? 60}%</span></div>
                <input type="range" min="0" max="95" step="5" data-testid="bg-lighten" value={data.bgLighten ?? 60} onChange={(e) => updateField("bgLighten", parseInt(e.target.value, 10))} style={{ accentColor: c.primary }} />
                <span className="text-[10px] text-neutral-400">0% = full picture, higher = lighter. Add "Overall invitation background" below to use it.</span>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-[11px] text-neutral-500">Intro video (plays instead of the Bismillah page)</Label>
                {data.introVideo && (
                  <video src={data.introVideo} muted playsInline className="w-full max-h-40 rounded-lg object-cover self-start" />
                )}
                <Input dir="auto" data-testid="editor-introVideo" value={data.introVideo || ""} onChange={(e) => updateField("introVideo", e.target.value)} className="text-xs" placeholder="Video URL (mp4)" />
                <div className="flex gap-3 items-center">
                  <label className="text-xs underline cursor-pointer" style={{ color: c.secondary }}>Upload video
                    <input type="file" accept="video/*" className="hidden" data-testid="upload-introVideo" onChange={(e) => e.target.files[0] && uploadVideo(e.target.files[0])} />
                  </label>
                  {data.introVideo && <button className="text-xs underline" style={{ color: c.secondary }} onClick={() => updateField("introVideo", "")}>Remove video</button>}
                </div>
                <span className="text-[10px] text-neutral-400">MP4 recommended, up to 40MB. Plays muted &amp; looping.</span>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-[11px] text-neutral-500">Photo below couple names</Label>
                {data.couplePhoto && <img src={data.couplePhoto} alt="" className="h-20 w-20 object-cover rounded self-start" />}
                <Input dir="auto" data-testid="editor-couplePhoto" value={data.couplePhoto || ""} onChange={(e) => updateField("couplePhoto", e.target.value)} className="text-xs" placeholder="Image URL" />
                <div className="flex gap-3"><label className="text-xs underline cursor-pointer" style={{ color: c.secondary }}>Upload image<input type="file" accept="image/*" className="hidden" data-testid="upload-couplePhoto" onChange={(e) => e.target.files[0] && uploadImg("couplePhoto", e.target.files[0])} /></label>{data.couplePhoto && <button className="text-xs underline" style={{ color: c.secondary }} onClick={() => updateField("couplePhoto", "")}>Remove</button>}</div>
              </div>
            </div>
            <div className="h-px bg-neutral-200" />
            <div className="flex flex-col gap-2">
              <Label className="text-[11px] tracking-wide text-neutral-500">Background music</Label>
              {MUSIC_PRESETS.map((m) => (
                <button key={m.name} data-testid="music-preset" onClick={() => updateField("musicUrl", m.url)} className="text-left text-xs rounded-md border px-3 py-2 hover:shadow-sm" style={{ borderColor: data.musicUrl === m.url ? c.secondary : "#e5e5e5" }}>{m.name}</button>
              ))}
              <Input dir="auto" data-testid="editor-musicUrl" value={data.musicUrl} onChange={(e) => updateField("musicUrl", e.target.value)} className="text-xs" placeholder="Music URL" />
              <label className="text-xs underline cursor-pointer" style={{ color: c.secondary }}>Upload audio<input type="file" accept="audio/*" className="hidden" data-testid="upload-audio" onChange={(e) => e.target.files[0] && uploadAudio(e.target.files[0])} /></label>
            </div>
          </TabsContent>
        </Tabs>

        <div className="sticky bottom-0 bg-white/90 backdrop-blur-xl border-t px-5 py-4 flex flex-col gap-3">
          {backups !== null && (
            <div className="rounded-lg border max-h-56 overflow-y-auto" data-testid="backups-list">
              <div className="px-3 py-2 text-[11px] tracking-wide text-neutral-500 border-b sticky top-0 bg-white">Auto-saved versions (newest first)</div>
              {backups.length === 0 ? (
                <p className="px-3 py-3 text-xs text-neutral-500">No backups yet. One is saved automatically every time you Save or Reset.</p>
              ) : (
                backups.map((b) => (
                  <div key={b.backupId} className="flex items-center justify-between gap-2 px-3 py-2 border-b last:border-0">
                    <div className="flex flex-col">
                      <span className="text-xs" style={{ color: c.text }}>{fmt(b.createdAt)}</span>
                      <span className="text-[10px] uppercase tracking-wide text-neutral-400">{b.reason}</span>
                    </div>
                    <button data-testid={`restore-${b.backupId}`} disabled={restoring} onClick={() => doRestore(b.backupId)} className="text-[11px] px-2.5 py-1 rounded-md text-white disabled:opacity-50" style={{ background: c.primary }}>Restore</button>
                  </div>
                ))
              )}
            </div>
          )}
          <div className="flex gap-3">
            <Button variant="outline" onClick={openBackups} data-testid="editor-backups-btn" className="flex-1">{backups !== null ? "Hide versions" : "Restore version"}</Button>
            <Button variant="outline" onClick={reset} data-testid="editor-reset-btn" className="flex-1">Reset</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="editor-save-btn" className="flex-1" style={{ background: c.primary }}>{saving ? "Saving..." : "Save & Publish"}</Button>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={exportContent} data-testid="editor-export-btn" className="flex-1 text-xs">⬇ Export content</Button>
            <label className="flex-1">
              <Button variant="outline" data-testid="editor-import-btn" className="w-full text-xs" asChild>
                <span>⬆ Import content</span>
              </Button>
              <input type="file" accept="application/json,.json" className="hidden" data-testid="import-file" onChange={(e) => e.target.files[0] && importContent(e.target.files[0])} />
            </label>
          </div>
          <p className="text-[10px] text-neutral-400 text-center">Export here, then Import on your live site to copy everything over.</p>
        </div>
      </SheetContent>
    </Sheet>
  );
};
